// Whiteboard data model - strokes are the source of truth, the canvas is only a renderer.

export type ToolId = 'hand' | 'pen' | 'stroke-eraser' | 'text' | 'select';

/** A sampled input point in board space. `p` is pressure 0..1 (0.5 when the device has none). */
export interface Point {
  x: number;
  y: number;
  p: number;
}

/** Axis-aligned bounding box in board space. */
export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface Stroke {
  id: string;
  tool: 'pen';
  color: string;
  /** Nominal ink width in board units. */
  size: number;
  points: Point[];
  /** Cached AABB of the raw points, not inflated by size. */
  bounds: Bounds;
}

/** Board space -> screen space: screen = scale * board + translate. */
export interface ViewTransform {
  x: number;
  y: number;
  scale: number;
}

/** A block of typed text anchored at its top-left corner in board space. */
export interface TextObject {
  id: string;
  x: number;
  y: number;
  color: string;
  /** Font size in board units. */
  size: number;
  /** May contain newlines; rendered as consecutive lines. */
  text: string;
  /** Cached AABB, measured when the object is created (see text.ts). */
  bounds: Bounds;
}

/** One board page. The document is an ordered list of these; each owns its own content. */
export interface BoardPage {
  id: string;
  strokes: Stroke[];
  texts: TextObject[];
}

export const IDENTITY_VIEW: ViewTransform = { x: 0, y: 0, scale: 1 };

let idCounter = 0;

/** Monotonic local id - no crypto.randomUUID (missing on old WebViews). */
export function createId(prefix: string): string {
  idCounter += 1;
  return prefix + Date.now().toString(36) + '-' + idCounter.toString(36);
}

export function createPage(): BoardPage {
  return { id: createId('p'), strokes: [], texts: [] };
}

// --- view transform ---------------------------------------------------------

export function screenToBoardX(view: ViewTransform, sx: number): number {
  return (sx - view.x) / view.scale;
}

export function screenToBoardY(view: ViewTransform, sy: number): number {
  return (sy - view.y) / view.scale;
}

export function boardToScreenX(view: ViewTransform, bx: number): number {
  return bx * view.scale + view.x;
}

export function boardToScreenY(view: ViewTransform, by: number): number {
  return by * view.scale + view.y;
}

/** Pan by a screen-space delta. */
export function panView(view: ViewTransform, dx: number, dy: number): ViewTransform {
  return { x: view.x + dx, y: view.y + dy, scale: view.scale };
}

/** Zoom around a screen-space anchor, keeping that point fixed. */
export function zoomViewAt(
  view: ViewTransform,
  factor: number,
  sx: number,
  sy: number,
  minScale = 0.25,
  maxScale = 4,
): ViewTransform {
  const scale = Math.min(maxScale, Math.max(minScale, view.scale * factor));
  const k = scale / view.scale;
  return { x: sx - (sx - view.x) * k, y: sy - (sy - view.y) * k, scale };
}

// --- bounds -----------------------------------------------------------------

export function emptyBounds(): Bounds {
  return { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
}

/** Mutates `b` - hot path, called per input point. */
export function expandBounds(b: Bounds, x: number, y: number): void {
  if (x < b.minX) b.minX = x;
  if (y < b.minY) b.minY = y;
  if (x > b.maxX) b.maxX = x;
  if (y > b.maxY) b.maxY = y;
}

export function boundsContains(b: Bounds, x: number, y: number, pad: number): boolean {
  return x >= b.minX - pad && x <= b.maxX + pad && y >= b.minY - pad && y <= b.maxY + pad;
}

export function boundsIntersect(a: Bounds, b: Bounds, pad: number): boolean {
  return !(
    a.maxX + pad < b.minX ||
    a.minX - pad > b.maxX ||
    a.maxY + pad < b.minY ||
    a.minY - pad > b.maxY
  );
}

export function isEmptyBounds(b: Bounds): boolean {
  return b.minX > b.maxX;
}

/** Grows `out` to cover `b`. Empty inputs are ignored. */
export function unionBounds(out: Bounds, b: Bounds): void {
  if (isEmptyBounds(b)) return;
  if (b.minX < out.minX) out.minX = b.minX;
  if (b.minY < out.minY) out.minY = b.minY;
  if (b.maxX > out.maxX) out.maxX = b.maxX;
  if (b.maxY > out.maxY) out.maxY = b.maxY;
}

/** Normalised box from two corners - the marquee drag produces either winding. */
export function boundsFromCorners(x0: number, y0: number, x1: number, y1: number): Bounds {
  return {
    minX: Math.min(x0, x1),
    minY: Math.min(y0, y1),
    maxX: Math.max(x0, x1),
    maxY: Math.max(y0, y1),
  };
}

export function boundsWidth(b: Bounds): number {
  return isEmptyBounds(b) ? 0 : b.maxX - b.minX;
}

export function boundsHeight(b: Bounds): number {
  return isEmptyBounds(b) ? 0 : b.maxY - b.minY;
}

/** AABB of a page, empty bounds when the page has nothing on it. */
export function pageBounds(page: BoardPage): Bounds {
  const out = emptyBounds();
  for (let i = 0; i < page.strokes.length; i++) unionBounds(out, page.strokes[i].bounds);
  for (let i = 0; i < page.texts.length; i++) unionBounds(out, page.texts[i].bounds);
  return out;
}

// --- stroke construction ----------------------------------------------------

export function createStroke(color: string, size: number, first: Point): Stroke {
  const bounds = emptyBounds();
  expandBounds(bounds, first.x, first.y);
  return {
    id: createId('s'),
    tool: 'pen',
    color,
    size,
    points: [first],
    bounds,
  };
}

/**
 * Appends a point to the active stroke, dropping samples closer than `minDist`.
 * Mutates the stroke - this is the 60fps ink path, not a document edit.
 * Returns true when the point was kept.
 */
export function pushStrokePoint(stroke: Stroke, x: number, y: number, p: number, minDist: number): boolean {
  const pts = stroke.points;
  const last = pts[pts.length - 1];
  const dx = x - last.x;
  const dy = y - last.y;
  if (dx * dx + dy * dy < minDist * minDist) return false;
  pts.push({ x, y, p });
  expandBounds(stroke.bounds, x, y);
  return true;
}

// --- hit testing ------------------------------------------------------------

function distToSegmentSq(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const vx = bx - ax;
  const vy = by - ay;
  const lenSq = vx * vx + vy * vy;
  let t = 0;
  if (lenSq > 0) {
    t = ((px - ax) * vx + (py - ay) * vy) / lenSq;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;
  }
  const dx = px - (ax + t * vx);
  const dy = py - (ay + t * vy);
  return dx * dx + dy * dy;
}

/** True when (x, y) is within `radius` of the stroke's centreline, inflated by half its width. */
export function isPointNearStroke(stroke: Stroke, x: number, y: number, radius: number): boolean {
  const reach = radius + stroke.size / 2;
  if (!boundsContains(stroke.bounds, x, y, reach)) return false;
  const pts = stroke.points;
  const reachSq = reach * reach;
  if (pts.length === 1) {
    const dx = x - pts[0].x;
    const dy = y - pts[0].y;
    return dx * dx + dy * dy <= reachSq;
  }
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (distToSegmentSq(x, y, a.x, a.y, b.x, b.y) <= reachSq) return true;
  }
  return false;
}

/** Topmost stroke under the point, or null. Iterates back to front (last drawn wins). */
export function hitTestStrokes(
  strokes: Stroke[],
  x: number,
  y: number,
  radius: number,
): Stroke | null {
  for (let i = strokes.length - 1; i >= 0; i--) {
    if (isPointNearStroke(strokes[i], x, y, radius)) return strokes[i];
  }
  return null;
}

/** Topmost text block whose box contains the point, or null. */
export function hitTestTexts(
  texts: TextObject[],
  x: number,
  y: number,
  pad: number,
): TextObject | null {
  for (let i = texts.length - 1; i >= 0; i--) {
    const t = texts[i];
    if (boundsContains(t.bounds, x, y, pad)) return t;
  }
  return null;
}

// --- selection ----------------------------------------------------------------

/** Ids of everything on the page whose box intersects `rect`. Strokes first, then texts. */
export function idsIntersecting(page: BoardPage, rect: Bounds): string[] {
  const out: string[] = [];
  for (let i = 0; i < page.strokes.length; i++) {
    const s = page.strokes[i];
    if (isEmptyBounds(s.bounds)) continue;
    if (boundsIntersect(s.bounds, rect, s.size / 2)) out.push(s.id);
  }
  for (let i = 0; i < page.texts.length; i++) {
    const t = page.texts[i];
    if (boundsIntersect(t.bounds, rect, 0)) out.push(t.id);
  }
  return out;
}

/** AABB of the selected items, empty bounds when nothing matches. */
export function selectionBounds(page: BoardPage, ids: string[]): Bounds {
  const out = emptyBounds();
  if (ids.length === 0) return out;
  const set = idSet(ids);
  for (let i = 0; i < page.strokes.length; i++) {
    const s = page.strokes[i];
    if (set[s.id]) unionBounds(out, s.bounds);
  }
  for (let i = 0; i < page.texts.length; i++) {
    const t = page.texts[i];
    if (set[t.id]) unionBounds(out, t.bounds);
  }
  return out;
}

/** Plain-object lookup - avoids leaning on Set iteration quirks in old WebViews. */
export function idSet(ids: string[]): { [id: string]: true } {
  const set: { [id: string]: true } = {};
  for (let i = 0; i < ids.length; i++) set[ids[i]] = true;
  return set;
}

export function translateBounds(b: Bounds, dx: number, dy: number): Bounds {
  if (isEmptyBounds(b)) return b;
  return { minX: b.minX + dx, minY: b.minY + dy, maxX: b.maxX + dx, maxY: b.maxY + dy };
}

/** Copy of the stroke shifted in board space - selection moves are immutable edits. */
export function translateStroke(stroke: Stroke, dx: number, dy: number): Stroke {
  const pts = new Array<Point>(stroke.points.length);
  for (let i = 0; i < stroke.points.length; i++) {
    const p = stroke.points[i];
    pts[i] = { x: p.x + dx, y: p.y + dy, p: p.p };
  }
  return {
    id: stroke.id,
    tool: stroke.tool,
    color: stroke.color,
    size: stroke.size,
    points: pts,
    bounds: translateBounds(stroke.bounds, dx, dy),
  };
}

export function translateText(t: TextObject, dx: number, dy: number): TextObject {
  return {
    id: t.id,
    x: t.x + dx,
    y: t.y + dy,
    color: t.color,
    size: t.size,
    text: t.text,
    bounds: translateBounds(t.bounds, dx, dy),
  };
}
